import {
  closestCenter,
  closestCorners,
  DndContext,
  DragOverlay,
  getFirstCollision,
  KeyboardCode,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type DroppableContainer,
  type KeyboardCoordinateGetter,
  type Modifier,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

// A reusable multi-container sortable engine (kanban columns, grouped lists).
// UX model: no insertion cursor — siblings displace to make room via CSS
// transforms while a DragOverlay clone follows the pointer. Cross-container
// transfers happen live in onDragOver (the item joins the hovered container
// mid-drag), following dnd-kit's MultipleContainers reference pattern.

export type DndContainerState = {
  itemIds: string[];
  key: string;
};

export type MultiContainerDropResult = {
  // Final ordering of every container after the drop.
  containers: DndContainerState[];
  itemId: string;
  sourceContainerKey: string;
  targetContainerKey: string;
};

type EngineContextValue = {
  wasRecentlyDragged: (itemId: string) => boolean;
};

const EngineContext = createContext<EngineContextValue>({ wasRecentlyDragged: () => false });

const recentDragSuppressionMs = 200;

export function MultiContainerDnd({
  children,
  containers,
  modifiers,
  onDragEnd,
  onDragStart,
  renderOverlay,
}: {
  children: (containers: DndContainerState[], activeItemId: string | null) => ReactNode;
  containers: DndContainerState[];
  modifiers?: Modifier[];
  // Called with null when the drag was cancelled or landed as a no-op.
  onDragEnd: (result: MultiContainerDropResult | null) => void;
  onDragStart?: (itemId: string) => void;
  // Without an overlay the source element itself follows the pointer —
  // the right mode for table/list rows, where a detached clone of a row
  // loses its grid columns.
  renderOverlay?: (itemId: string) => ReactNode;
}) {
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [dragContainers, setDragContainers] = useState<DndContainerState[] | null>(null);
  const sourceContainerKeyRef = useRef<string | null>(null);
  const startContainersRef = useRef<DndContainerState[]>(containers);
  const lastOverIdRef = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainerRef = useRef(false);
  const recentDragRef = useRef<{ at: number; itemId: string } | null>(null);

  // Containers from props keep updating underneath an active drag (live
  // events, refetches); the drag operates on its own snapshot until it ends.
  const effectiveContainers = dragContainers ?? containers;
  const effectiveContainersRef = useRef(effectiveContainers);
  effectiveContainersRef.current = effectiveContainers;

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainerRef.current = false;
    });
  }, [effectiveContainers]);

  const sensors = useSensors(
    // The distance threshold keeps plain clicks (open card, inner controls)
    // from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: multiContainerCoordinateGetter }),
  );

  const findContainerKey = useCallback((id: UniqueIdentifier, state: DndContainerState[]) => {
    const key = String(id);
    if (state.some((container) => container.key === key)) {
      return key;
    }
    return state.find((container) => container.itemIds.includes(key))?.key ?? null;
  }, []);

  // Containers first (pointer-based, so hovering a column always resolves),
  // then the closest item inside the resolved container. Falls back to the
  // last known target right after a cross-container transfer, when the layout
  // shifts under the pointer and would otherwise flicker between targets.
  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    const intersections = pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
    let overId = getFirstCollision(intersections, "id");

    if (overId != null) {
      const state = effectiveContainersRef.current;
      const container = state.find((candidate) => candidate.key === String(overId));
      if (container && container.itemIds.length > 0) {
        const closestItem = closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (droppable) => droppable.id !== overId && container.itemIds.includes(String(droppable.id)),
          ),
        })[0]?.id;
        if (closestItem != null) {
          overId = closestItem;
        }
      }
      lastOverIdRef.current = overId;
      return [{ id: overId }];
    }

    return lastOverIdRef.current != null && recentlyMovedToNewContainerRef.current
      ? [{ id: lastOverIdRef.current }]
      : [];
  }, []);

  function handleDragStart(event: DragStartEvent) {
    const itemId = String(event.active.id);
    const snapshot = containers.map((container) => ({ ...container, itemIds: [...container.itemIds] }));
    startContainersRef.current = snapshot;
    sourceContainerKeyRef.current = findContainerKey(itemId, snapshot);
    lastOverIdRef.current = null;
    setDragContainers(snapshot);
    setActiveItemId(itemId);
    onDragStart?.(itemId);
  }

  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) {
      return;
    }
    setDragContainers((current) => {
      if (!current) {
        return current;
      }
      const activeId = String(active.id);
      const activeKey = findContainerKey(activeId, current);
      const overKey = findContainerKey(over.id, current);
      if (!activeKey || !overKey || activeKey === overKey) {
        return current;
      }

      const overContainer = current.find((container) => container.key === overKey);
      if (!overContainer) {
        return current;
      }
      const overIndex = overContainer.itemIds.indexOf(String(over.id));
      let insertIndex: number;
      if (overIndex >= 0) {
        const isBelowOverItem =
          active.rect.current.translated != null &&
          active.rect.current.translated.top > over.rect.top + over.rect.height;
        insertIndex = overIndex + (isBelowOverItem ? 1 : 0);
      } else {
        insertIndex = overContainer.itemIds.length;
      }

      recentlyMovedToNewContainerRef.current = true;
      return current.map((container) => {
        if (container.key === activeKey) {
          return { ...container, itemIds: container.itemIds.filter((id) => id !== activeId) };
        }
        if (container.key === overKey) {
          const itemIds = container.itemIds.filter((id) => id !== activeId);
          itemIds.splice(insertIndex, 0, activeId);
          return { ...container, itemIds };
        }
        return container;
      });
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    const state = dragContainers ?? containers;
    const activeId = String(active.id);
    const sourceKey = sourceContainerKeyRef.current;
    recentDragRef.current = { at: Date.now(), itemId: activeId };

    let result: MultiContainerDropResult | null = null;
    if (over && sourceKey) {
      const targetKey = findContainerKey(activeId, state);
      let finalContainers = state;
      if (targetKey && targetKey === findContainerKey(over.id, state) && String(over.id) !== targetKey) {
        const container = state.find((candidate) => candidate.key === targetKey);
        const fromIndex = container?.itemIds.indexOf(activeId) ?? -1;
        const toIndex = container?.itemIds.indexOf(String(over.id)) ?? -1;
        if (container && fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
          finalContainers = state.map((candidate) =>
            candidate.key === targetKey
              ? { ...candidate, itemIds: arrayMove(candidate.itemIds, fromIndex, toIndex) }
              : candidate,
          );
        }
      }
      const changed = containersSignature(finalContainers) !== containersSignature(startContainersRef.current);
      if (targetKey && changed) {
        result = {
          containers: finalContainers,
          itemId: activeId,
          sourceContainerKey: sourceKey,
          targetContainerKey: targetKey,
        };
      }
    }

    setDragContainers(null);
    setActiveItemId(null);
    lastOverIdRef.current = null;
    sourceContainerKeyRef.current = null;
    onDragEnd(result);
  }

  function handleDragCancel() {
    setDragContainers(null);
    setActiveItemId(null);
    lastOverIdRef.current = null;
    sourceContainerKeyRef.current = null;
    onDragEnd(null);
  }

  const engineValue = useMemo<EngineContextValue>(
    () => ({
      wasRecentlyDragged: (itemId) => {
        const recent = recentDragRef.current;
        return recent !== null && recent.itemId === itemId && Date.now() - recent.at < recentDragSuppressionMs;
      },
    }),
    [],
  );

  return (
    <EngineContext.Provider value={engineValue}>
      <DndContext
        collisionDetection={collisionDetection}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        modifiers={modifiers}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        sensors={sensors}
      >
        {children(effectiveContainers, activeItemId)}
        {renderOverlay
          ? createPortal(
              <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.2, 0, 0, 1)" }}>
                {activeItemId ? renderOverlay(activeItemId) : null}
              </DragOverlay>,
              document.body,
            )
          : null}
      </DndContext>
    </EngineContext.Provider>
  );
}

// A single vertical sortable list (settings rows, dialog options): one
// container, no overlay (the row itself follows the pointer), movement locked
// to the vertical axis.
export function DndSortableVerticalList({
  children,
  itemIds,
  onReorder,
}: {
  children: ReactNode;
  itemIds: string[];
  onReorder: (result: { itemId: string; orderedItemIds: string[] }) => void;
}) {
  return (
    <MultiContainerDnd
      containers={[{ itemIds, key: verticalListContainerKey }]}
      modifiers={verticalListModifiers}
      onDragEnd={(result) => {
        const orderedItemIds = result?.containers[0]?.itemIds;
        if (result && orderedItemIds) {
          onReorder({ itemId: result.itemId, orderedItemIds });
        }
      }}
    >
      {() => <DndSortableList itemIds={itemIds}>{children}</DndSortableList>}
    </MultiContainerDnd>
  );
}

const verticalListContainerKey = "__vertical-list__";
const verticalListModifiers = [restrictToVerticalAxis];

export function DndSortableList({ children, itemIds }: { children: ReactNode; itemIds: string[] }) {
  // SortableContext must see the SAME array identity until the ids actually
  // change: consumers re-render on every over/active update and would other-
  // wise hand it a fresh array each time, which trips useSortable's
  // "items changed" guard and replaces the displacement transition with a
  // 0ms one — siblings then snap instead of gliding.
  const stableItemIds = useShallowStableArray(itemIds);
  return (
    <SortableContext items={stableItemIds} strategy={verticalListSortingStrategy}>
      {children}
    </SortableContext>
  );
}

function useShallowStableArray(value: string[]) {
  const ref = useRef(value);
  if (
    ref.current !== value &&
    (ref.current.length !== value.length || value.some((entry, index) => ref.current[index] !== entry))
  ) {
    ref.current = value;
  }
  return ref.current;
}

export function useDndContainer(key: string, { childCount }: { childCount: number }) {
  const { isOver, setNodeRef } = useDroppable({
    data: { childCount, type: "container" },
    id: key,
  });
  return { isOver, setNodeRef };
}

export function useDndSortableItem(id: string, { disabled = false }: { disabled?: boolean } = {}) {
  const { wasRecentlyDragged } = useContext(EngineContext);
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    data: { type: "item" },
    disabled,
    id,
  });
  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  return {
    dragProps: disabled ? undefined : { ...attributes, ...listeners },
    isDragSource: isDragging,
    setNodeRef,
    style,
    // Browsers still fire a click on the drag source after the pointer is
    // released; callers use this to keep that click from opening the item.
    wasRecentlyDragged: () => wasRecentlyDragged(id),
  };
}

function containersSignature(containers: DndContainerState[]) {
  return containers.map((container) => `${container.key}:${container.itemIds.join(",")}`).join("|");
}

const keyboardDirections: string[] = [KeyboardCode.Down, KeyboardCode.Right, KeyboardCode.Up, KeyboardCode.Left];

// Adapted from dnd-kit's MultipleContainers coordinate getter: arrows target
// the closest droppable in the pressed direction, letting keyboard drags
// cross containers (including empty ones, which have no sortable items).
const multiContainerCoordinateGetter: KeyboardCoordinateGetter = (
  event,
  { context: { active, collisionRect, droppableContainers, droppableRects } },
) => {
  if (!keyboardDirections.includes(event.code)) {
    return undefined;
  }
  event.preventDefault();
  if (!active || !collisionRect) {
    return undefined;
  }

  const filteredContainers: DroppableContainer[] = [];
  droppableContainers.getEnabled().forEach((entry) => {
    if (!entry || entry.disabled) {
      return;
    }
    const rect = droppableRects.get(entry.id);
    if (!rect) {
      return;
    }
    const data = entry.data.current as { childCount?: number; type?: string } | undefined;
    // Non-empty containers resolve through their items instead.
    if (data?.type === "container" && (data.childCount ?? 0) > 0) {
      return;
    }
    switch (event.code) {
      case KeyboardCode.Down:
        if (collisionRect.top < rect.top) {
          filteredContainers.push(entry);
        }
        break;
      case KeyboardCode.Up:
        if (collisionRect.top > rect.top) {
          filteredContainers.push(entry);
        }
        break;
      case KeyboardCode.Left:
        if (collisionRect.left >= rect.left + rect.width) {
          filteredContainers.push(entry);
        }
        break;
      case KeyboardCode.Right:
        if (collisionRect.left + collisionRect.width <= rect.left) {
          filteredContainers.push(entry);
        }
        break;
    }
  });

  const collisions = closestCorners({
    active,
    collisionRect,
    droppableContainers: filteredContainers,
    droppableRects,
    pointerCoordinates: null,
  });
  const closestId = getFirstCollision(collisions, "id");
  if (closestId == null) {
    return undefined;
  }

  const newDroppable = droppableContainers.get(closestId);
  const newNode = newDroppable?.node.current;
  const newRect = newDroppable?.rect.current;
  if (!newNode || !newRect) {
    return undefined;
  }
  const data = newDroppable.data.current as { type?: string } | undefined;
  if (data?.type === "container") {
    return { x: newRect.left + 20, y: newRect.top + 74 };
  }
  return { x: newRect.left, y: newRect.top };
};
